import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { breakevenService } from '../../../services/financeService';
import { BreakevenAnalysis } from '../../../types/finance';
import { FaSave, FaTrash, FaPlus } from 'react-icons/fa';
import toast from 'react-hot-toast';

const BreakEvenTab: React.FC = () => {
  const { user } = useAuth();
  const [analyses, setAnalyses] = useState<BreakevenAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState<BreakevenAnalysis>({
    name: '',
    product_name: '',
    fixed_costs: 0,
    variable_cost_per_unit: 0,
    selling_price_per_unit: 0,
    contribution_margin: 0,
    breakeven_units: 0,
    breakeven_revenue: 0,
  });

  useEffect(() => {
    loadAnalyses();
  }, []);

  const loadAnalyses = async () => {
    try {
      setLoading(true);
      const data = await breakevenService.getAll();
      setAnalyses(data);
    } catch (error) {
      console.error('Error loading breakeven analyses:', error);
      toast.error('Failed to load breakeven analyses');
    } finally {
      setLoading(false);
    }
  };

  const calculateBreakeven = (analysis: BreakevenAnalysis): { 
    contributionMargin: number; 
    breakevenUnits: number; 
    breakevenRevenue: number;
  } => {
    const { fixed_costs, variable_cost_per_unit, selling_price_per_unit } = analysis;
    const contributionMargin = selling_price_per_unit - variable_cost_per_unit;
    const breakevenUnits = contributionMargin > 0 ? fixed_costs / contributionMargin : 0;
    const breakevenRevenue = breakevenUnits * selling_price_per_unit;
    
    return {
      contributionMargin: parseFloat(contributionMargin.toFixed(2)),
      breakevenUnits: Math.ceil(breakevenUnits),
      breakevenRevenue: parseFloat(breakevenRevenue.toFixed(2))
    };
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let parsedValue: string | number = value;
    
    if (['fixed_costs', 'variable_cost_per_unit', 'selling_price_per_unit'].includes(name)) {
      parsedValue = parseFloat(value) || 0;
    }
    
    setCurrentAnalysis(prev => ({ ...prev, [name]: parsedValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { contributionMargin, breakevenUnits, breakevenRevenue } = calculateBreakeven(currentAnalysis);
      const analysisToSave = {
        ...currentAnalysis,
        user_id: user?.id,
        contribution_margin: contributionMargin,
        breakeven_units: breakevenUnits,
        breakeven_revenue: breakevenRevenue
      };
      
      if (currentAnalysis.id) {
        await breakevenService.update(currentAnalysis.id, analysisToSave);
        toast.success('Analysis updated');
      } else {
        await breakevenService.create(analysisToSave);
        toast.success('Analysis created');
      }
      
      setShowForm(false);
      setCurrentAnalysis({
        name: '',
        product_name: '',
        fixed_costs: 0,
        variable_cost_per_unit: 0,
        selling_price_per_unit: 0,
        contribution_margin: 0,
        breakeven_units: 0,
        breakeven_revenue: 0,
      });
      loadAnalyses();
    } catch (error) {
      console.error('Error saving break-even analysis:', error);
      toast.error('Failed to save analysis');
    }
  };

  const handleEdit = (analysis: BreakevenAnalysis) => {
    setCurrentAnalysis(analysis);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Delete this analysis?')) {
      try {
        await breakevenService.delete(id);
        toast.success('Analysis deleted');
        loadAnalyses();
      } catch (error) {
        console.error('Error deleting analysis:', error);
        toast.error('Failed to delete analysis');
      }
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading analyses...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Info Box */}
      <div className="rounded-xl bg-blue-50 p-5 shadow-sm ring-1 ring-blue-100">
        <h3 className="mb-2 text-base font-semibold text-blue-900">Break-Even Analysis</h3>
        <p className="mb-3 text-sm text-blue-800">
          Calculate how many units you need to sell to cover costs and start making profit.
        </p>
        <ul className="space-y-1 text-sm text-blue-700">
          <li><strong>Fixed Costs:</strong> Expenses that don't change (rent, salaries)</li>
          <li><strong>Variable Costs:</strong> Costs per unit (materials, labor)</li>
          <li><strong>Contribution Margin:</strong> Amount each unit contributes to covering fixed costs</li>
        </ul>
      </div>

      {/* Create Button */}
      {!showForm && (
        <button
          onClick={() => {
            setCurrentAnalysis({
              name: '',
              product_name: '',
              fixed_costs: 0,
              variable_cost_per_unit: 0,
              selling_price_per_unit: 0,
              contribution_margin: 0,
              breakeven_units: 0,
              breakeven_revenue: 0,
            });
            setShowForm(true);
          }}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
        >
          <FaPlus /> Create New Analysis
        </button>
      )}

      {/* Form */}
      {showForm && (
        <div className="rounded-xl bg-gray-50 p-6 shadow-sm ring-1 ring-gray-200">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">
            {currentAnalysis.id ? 'Edit' : 'Create'} Break-Even Analysis
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Analysis Name *</label>
                <input
                  type="text"
                  name="name"
                  value={currentAnalysis.name}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="e.g., New Product Launch"
                  required
                />
              </div>
              
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Product/Service *</label>
                <input
                  type="text"
                  name="product_name"
                  value={currentAnalysis.product_name}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="e.g., Handcrafted Candles"
                  required
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Fixed Costs ($) *</label>
                <input
                  type="number"
                  name="fixed_costs"
                  value={currentAnalysis.fixed_costs}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="1000.00"
                  min="0"
                  step="0.01"
                  required
                />
                <span className="mt-1 block text-xs text-gray-500">Monthly costs that don't vary</span>
              </div>
              
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Variable Cost/Unit ($) *</label>
                <input
                  type="number"
                  name="variable_cost_per_unit"
                  value={currentAnalysis.variable_cost_per_unit}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="5.00"
                  min="0"
                  step="0.01"
                  required
                />
                <span className="mt-1 block text-xs text-gray-500">Cost per additional unit</span>
              </div>
              
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Selling Price/Unit ($) *</label>
                <input
                  type="number"
                  name="selling_price_per_unit"
                  value={currentAnalysis.selling_price_per_unit}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="15.00"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                name="notes"
                value={currentAnalysis.notes || ''}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="Additional information"
                rows={2}
              />
            </div>

            {/* Results Preview */}
            {currentAnalysis.fixed_costs > 0 && 
             currentAnalysis.selling_price_per_unit > 0 && 
             currentAnalysis.variable_cost_per_unit < currentAnalysis.selling_price_per_unit && (
              <div className="rounded-xl bg-green-50 p-5 shadow-sm ring-1 ring-green-200">
                <h4 className="mb-3 text-base font-semibold text-green-900">Break-Even Results</h4>
                
                {(() => {
                  const { contributionMargin, breakevenUnits, breakevenRevenue } = calculateBreakeven(currentAnalysis);
                  
                  return (
                    <div className="grid gap-3 md:grid-cols-3">
                      <div className="rounded-lg bg-white p-3">
                        <p className="text-xs text-green-700">Contribution Margin</p>
                        <p className="text-lg font-bold text-green-600">${contributionMargin.toFixed(2)}</p>
                        <p className="text-xs text-gray-600">per unit</p>
                      </div>
                      
                      <div className="rounded-lg bg-white p-3">
                        <p className="text-xs text-blue-700">Break-Even Units</p>
                        <p className="text-lg font-bold text-blue-600">{breakevenUnits.toLocaleString()}</p>
                        <p className="text-xs text-gray-600">units to sell</p>
                      </div>
                      
                      <div className="rounded-lg bg-white p-3">
                        <p className="text-xs text-purple-700">Break-Even Revenue</p>
                        <p className="text-lg font-bold text-purple-600">${breakevenRevenue.toLocaleString()}</p>
                        <p className="text-xs text-gray-600">total sales needed</p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Warning for invalid pricing */}
            {currentAnalysis.variable_cost_per_unit >= currentAnalysis.selling_price_per_unit && 
             currentAnalysis.selling_price_per_unit > 0 && (
              <div className="rounded-xl bg-red-50 p-4 shadow-sm ring-1 ring-red-200">
                <p className="text-sm font-semibold text-red-900">⚠️ Pricing Issue</p>
                <p className="mt-1 text-sm text-red-700">
                  Variable cost (${currentAnalysis.variable_cost_per_unit.toFixed(2)}) is ≥ selling price (${currentAnalysis.selling_price_per_unit.toFixed(2)}). 
                  You will never break even with this pricing.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2">
              {currentAnalysis.id && (
                <button
                  type="button"
                  onClick={() => handleDelete(currentAnalysis.id!)}
                  className="flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                >
                  <FaTrash /> Delete
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
              >
                <FaSave /> {currentAnalysis.id ? 'Update' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Analyses Table */}
      {analyses.length > 0 && (
        <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Fixed Costs</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Price/Unit</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Break-Even Units</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Break-Even Revenue</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {analyses.map((analysis) => (
                  <tr key={analysis.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{analysis.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{analysis.product_name}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="rounded-full bg-blue-50 px-2 py-1 text-blue-700">
                        ${analysis.fixed_costs.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="rounded-full bg-purple-50 px-2 py-1 text-purple-700">
                        ${analysis.selling_price_per_unit.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="rounded-full bg-green-50 px-2 py-1 font-medium text-green-700">
                        {analysis.breakeven_units.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="rounded-full bg-indigo-50 px-2 py-1 font-medium text-indigo-700">
                        ${analysis.breakeven_revenue.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(analysis)}
                          className="text-primary hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(analysis.id!)}
                          className="text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {analyses.length === 0 && !showForm && (
        <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
          <div className="text-4xl mb-4">⚖️</div>
          <h3 className="text-lg font-semibold text-gray-900">No Break-Even Analyses Yet</h3>
          <p className="mt-2 text-sm text-gray-600">
            Create your first analysis to calculate how many units you need to sell
          </p>
        </div>
      )}
    </div>
  );
};

export default BreakEvenTab;
